// src/pages/admin/GerenciamentoEventosPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import EventoForm from '../../components/admin/EventoForm';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { getAdminEventos, ativarEvento, desativarEvento, deleteEvento, desativarEventosFinalizados } from '../../services/api';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/Card';
import { PlusCircle, Edit, Trash2, Power, PowerOff, ListOrdered, Archive, Search, XCircle } from 'lucide-react';
import { Skeleton } from '../../components/ui/Skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Label } from '../../components/ui/Label';
import { Input } from '../../components/ui/Input';
import { DatePicker } from '../../components/ui/DatePicker';
import { isWithinInterval, parseISO, format } from 'date-fns';

// Função para determinar o texto do status do evento
const getEventStatusText = (evento) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normaliza para comparar apenas a data

    const eventEndDate = new Date(evento.dataFim);
    eventEndDate.setHours(0, 0, 0, 0); // Normaliza

    if (eventEndDate < today) {
        return 'Encerrado';
    } else if (!evento.ativo) {
        return 'Inativo';
    } else {
        return 'Ativo';
    }
};

// Função para determinar a classe de estilo do status
const getEventStatusClass = (statusText) => {
    switch (statusText) {
        case 'Ativo':
            return 'bg-green-100 text-green-800';
        case 'Inativo':
            return 'bg-red-100 text-red-800';
        case 'Encerrado':
            return 'bg-gray-100 text-gray-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

function GerenciamentoEventosPage() {
    const { participanteRole, logout } = useAuth();
    const navigate = useNavigate();
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [eventoSelecionado, setEventoSelecionado] = useState(null);
    const [isDesactivatingAll, setIsDesactivatingAll] = useState(false);

    // Estados para os novos filtros
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('Ativo'); // ALTERADO: Padrão para 'Ativo'
    const [filterStartDate, setFilterStartDate] = useState(undefined);
    const [filterEndDate, setFilterEndDate] = useState(undefined);

    const fetchEventos = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAdminEventos();
            setEventos(data);
        } catch (err) {
            console.error("Erro ao buscar eventos:", err);
            setError(err.message || "Não foi possível carregar os eventos.");
            if (err.message.includes('401') || err.message.includes('403')) {
                logout();
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    }, [logout, navigate]);

    useEffect(() => {
        if (participanteRole !== 'ROLE_ADMIN') {
            setError("Acesso negado. Apenas administradores podem gerenciar eventos.");
            setLoading(false);
            logout();
            navigate('/login');
            return;
        }
        fetchEventos();
    }, [participanteRole, logout, navigate, fetchEventos]);

    const handleEdit = (evento) => {
        setEventoSelecionado(evento);
        setShowForm(true);
    };

    const handleToggleAtivo = async (id, currentStatus) => {
        const actionText = currentStatus ? 'desativar' : 'ativar';
        const result = await Swal.fire({
            title: `Confirma ${actionText} este evento?`,
            text: `Você tem certeza que deseja ${actionText} o evento "${eventos.find(e => e.id === id)?.nome}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: currentStatus ? '#dc3545' : '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: `Sim, ${actionText}!`,
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                if (currentStatus) {
                    await desativarEvento(id);
                } else {
                    await ativarEvento(id);
                }
                await fetchEventos();
                toast.success(`Evento ${actionText === 'desativar' ? 'desativado' : 'ativado'} com sucesso!`);
            } catch (err) {
                console.error("Erro ao alterar status do evento:", err);
                toast.error(`Erro ao ${actionText} evento: ${err.message}`);
            }
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Tem certeza?',
            text: "Você não poderá reverter isso! Todas as inscrições e sessões ligadas a este evento serão afetadas.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, deletar!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deleteEvento(id);
                await fetchEventos();
                toast.success("Evento deletado com sucesso!");
            } catch (err) {
                console.error("Erro ao deletar evento:", err);
                toast.error(`Erro ao deletar evento: ${err.message}`);
            }
        }
    };

    const handleDesativarFinalizados = async () => {
        const result = await Swal.fire({
            title: 'Desativar Eventos Finalizados?',
            text: "Todos os eventos cuja data de fim já passou serão desativados. Esta ação não pode ser desfeita para cada evento individualmente.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, desativar todos!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            setIsDesactivatingAll(true);
            try {
                await desativarEventosFinalizados();
                await fetchEventos();
                toast.success("Todos os eventos finalizados foram desativados com sucesso!");
            } catch (err) {
                console.error("Erro ao desativar eventos finalizados:", err);
                toast.error(`Erro ao desativar eventos: ${err.message}`);
            } finally {
                setIsDesactivatingAll(false);
            }
        }
    };

    const handleFormSubmit = () => {
        toast.success(`Evento ${eventoSelecionado ? 'atualizado' : 'criado'} com sucesso!`);
        setShowForm(false);
        setEventoSelecionado(null);
        fetchEventos();
    };

    const handleManageSessoes = (eventoId) => {
        navigate(`/admin/eventos/${eventoId}/sessoes`);
    };

    // Lógica de filtragem dos eventos
    const filteredEvents = eventos.filter(evento => {
        // 1. Filtro por pesquisa de texto
        const matchesSearch = searchQuery === '' ||
                              evento.nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              evento.descricao.toLowerCase().includes(searchQuery.toLowerCase());

        // 2. Filtro por status
        const statusText = getEventStatusText(evento);
        const matchesStatus = filterStatus === 'todos' || statusText === filterStatus;

        // 3. Filtro por data de início
        const eventStartDate = parseISO(evento.dataInicio);
        const matchesStartDate = !filterStartDate || eventStartDate >= filterStartDate;

        // 4. Filtro por data de fim
        const eventEndDate = parseISO(evento.dataFim);
        const matchesEndDate = !filterEndDate || eventEndDate <= filterEndDate;

        return matchesSearch && matchesStatus && matchesStartDate && matchesEndDate;
    });

    // Variável para verificar se há filtros ativos
    const hasActiveFilters = searchQuery !== '' || filterStatus !== 'Ativo' || filterStartDate !== undefined || filterEndDate !== undefined;

    // Função para limpar todos os filtros
    const handleClearFilters = () => {
        setSearchQuery('');
        setFilterStatus('todos'); // Ao limpar, volta para 'todos'
        setFilterStartDate(undefined);
        setFilterEndDate(undefined);
    };

    const EventosTableSkeleton = () => (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="text-left">
                    <tr className="border-b">
                        <th className="p-4 font-medium">Nome</th>
                        <th className="p-4 font-medium hidden md:table-cell">Local</th>
                        <th className="p-4 font-medium hidden lg:table-cell">Período</th>
                        <th className="p-4 font-medium hidden md:table-cell">Status</th>
                        <th className="p-4 font-medium text-right">Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: 5 }).map((_, index) => (
                        <tr key={index} className="border-b">
                            <td className="p-4"><Skeleton className="h-5 w-3/4" /></td>
                            <td className="p-4 hidden md:table-cell"><Skeleton className="h-5 w-2/4" /></td>
                            <td className="p-4 hidden lg:table-cell"><Skeleton className="h-5 w-3/4" /></td>
                            <td className="p-4 hidden md:table-cell"><Skeleton className="h-6 w-16 rounded-full" /></td>
                            <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                <Skeleton className="h-8 w-8 inline-block" />
                                <Skeleton className="h-8 w-8 inline-block" />
                                <Skeleton className="h-8 w-8 inline-block" />
                                <Skeleton className="h-8 w-8 inline-block" />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    if (error) {
        return <div className="container mx-auto p-4 md:p-6 lg:p-8 text-destructive">Erro: {error}</div>;
    }

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <h1 className="text-3xl font-bold tracking-tight">Gerenciamento de Eventos</h1>
                <div className="flex flex-col sm:flex-row gap-2 flex-wrap justify-end w-full sm:w-auto">
                    <Button variant="outline" onClick={handleDesativarFinalizados} disabled={isDesactivatingAll} className="w-full sm:w-auto">
                        {isDesactivatingAll ? 'A desativar...' : <><Archive className="mr-2 h-4 w-4" /> Desativar Finalizados</>}
                    </Button>
                    <Button onClick={() => { setEventoSelecionado(null); setShowForm(true); }} className="w-full sm:w-auto">
                        <PlusCircle className="mr-2 h-4 w-4" /> Criar Novo Evento
                    </Button>
                </div>
            </div>

            {showForm && (
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle className="mb-2">{eventoSelecionado ? 'Editar Evento' : 'Criar Novo Evento'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <EventoForm
                            evento={eventoSelecionado}
                            onSuccess={handleFormSubmit}
                            onCancel={() => { setShowForm(false); setEventoSelecionado(null); }}
                        />
                    </CardContent>
                </Card>
            )}

            <Card className="mb-6">
                <CardHeader>
                    <CardTitle className="mb-2">Filtros</CardTitle>
                    <CardDescription className="leading-relaxed">Refine a sua busca por nome, descrição, status ou período do evento.</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Search input - always full width for prominence */}
                    <div className="relative col-span-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Pesquisar por nome ou descrição..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 w-full"
                        />
                    </div>

                    {/* Filter row - these three will share space */}
                    <div className="grid gap-2">
                        <Label htmlFor="filterStatus" className="mb-1">Status:</Label>
                        <Select onValueChange={setFilterStatus} value={filterStatus} className="w-full">
                            <SelectTrigger id="filterStatus">
                                <SelectValue placeholder="Todos os Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="todos">Todos os Status</SelectItem>
                                <SelectItem value="Ativo">Ativo</SelectItem>
                                <SelectItem value="Inativo">Inativo</SelectItem>
                                <SelectItem value="Encerrado">Encerrado</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="filterStartDate" className="mb-1">Data de Início (a partir de):</Label>
                        <DatePicker
                            date={filterStartDate}
                            setDate={setFilterStartDate}
                            placeholder="Data de início"
                            className="w-full"
                        />
                    </div>
                    <div className="grid gap-2">
                        <Label htmlFor="filterEndDate" className="mb-1">Data de Fim (até):</Label>
                        <DatePicker
                            date={filterEndDate}
                            setDate={setFilterEndDate}
                            placeholder="Data de fim"
                            className="w-full"
                        />
                    </div>
                </CardContent>
                <CardFooter className="flex justify-end pt-4">
                    {hasActiveFilters && (
                        <Button variant="outline" onClick={handleClearFilters} className="w-full sm:w-auto">
                            <XCircle className="mr-2 h-4 w-4" /> Limpar Filtros
                        </Button>
                    )}
                </CardFooter>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="mb-2">Lista de Eventos</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <EventosTableSkeleton />
                    ) : filteredEvents.length === 0 ? (
                        <div className="text-center py-12 border-2 border-dashed rounded-lg">
                            <h3 className="text-lg font-medium text-muted-foreground">Nenhum evento encontrado</h3>
                            <p className="text-sm text-muted-foreground mt-1 mb-4">
                                {filterStatus === 'todos' && searchQuery === '' && !filterStartDate && !filterEndDate
                                    ? 'Comece por criar o seu primeiro evento.'
                                    : 'Nenhum evento encontrado com os filtros aplicados.'}
                            </p>
                            {filterStatus === 'todos' && searchQuery === '' && !filterStartDate && !filterEndDate && (
                                <Button onClick={() => { setEventoSelecionado(null); setShowForm(true); }}>
                                    <PlusCircle className="mr-2 h-4 w-4" /> Criar Novo Evento
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-left">
                                    <tr className="border-b">
                                        <th className="p-4 font-medium">Nome</th>
                                        <th className="p-4 font-medium hidden md:table-cell">Local</th>
                                        <th className="p-4 font-medium hidden lg:table-cell">Período</th>
                                        <th className="p-4 font-medium hidden md:table-cell">Status</th>
                                        <th className="p-4 font-medium text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredEvents.map(evento => {
                                        const statusText = getEventStatusText(evento);
                                        const statusClass = getEventStatusClass(statusText);
                                        return (
                                            <tr key={evento.id} className="border-b hover:bg-accent/50 even:bg-muted/50 transition-colors">
                                                <td className="p-4 font-medium">{evento.nome}</td>
                                                <td className="p-4 text-muted-foreground hidden md:table-cell">{evento.local}</td>
                                                <td className="p-4 text-muted-foreground hidden lg:table-cell">
                                                    {new Date(evento.dataInicio).toLocaleDateString()} - {new Date(evento.dataFim).toLocaleDateString()}
                                                </td>
                                                <td className="p-4 hidden md:table-cell">
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusClass}`}>
                                                        {statusText}
                                                    </span>
                                                </td>
                                                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                                    <Button variant="outline" size="sm" onClick={() => handleEdit(evento)}><Edit className="h-4 w-4" /></Button>
                                                    {statusText !== 'Encerrado' && (
                                                        <Button variant="outline" size="sm" onClick={() => handleToggleAtivo(evento.id, evento.ativo)}>
                                                            {evento.ativo ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                                                        </Button>
                                                    )}
                                                    <Button variant="outline" size="sm" onClick={() => handleManageSessoes(evento.id)}><ListOrdered className="h-4 w-4" /></Button>
                                                    <Button variant="destructive" size="sm" onClick={() => handleDelete(evento.id)}><Trash2 className="h-4 w-4" /></Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export default GerenciamentoEventosPage;